/**
 * OaSIS Explorer Frontend - Occupation Utilities
 * 
 * Shared utility functions for extracting occupation metadata
 */

import React from 'react';
import { IdCard, Briefcase, FlaskConical, HeartPulse, Landmark, Palette, ShoppingCart, Wrench, Sprout, Cuboid } from 'lucide-react';
import { HIERARCHY_CATEGORIES } from '../hooks/useAdvancedSearch';

/**
 * Extract broad category from OaSIS code
 * @param oasisCode - OaSIS code (e.g., "82030.01")
 * @returns Broad category object with value and label, or null
 */
export const getBroadCategory = (oasisCode: string): { value: string; label: string } | null => {
  const firstDigit = oasisCode.charAt(0);
  return HIERARCHY_CATEGORIES.find(cat => cat.value === firstDigit) || null;
};

/**
 * Get icon for broad category by code (0-9)
 * @param code - Broad category code (0-9)
 * @param size - Icon size (default: 12)
 * @returns React node with icon, or null
 */
export const getBroadCategoryIcon = (code: string, size: number = 12): React.ReactNode | null => {
  const iconProps = { 
    size, 
    className: "text-gray-500 dark:text-gray-400",
    strokeWidth: 2 
  };
  
  const iconMap: Record<string, React.ReactNode> = {
    '0': <IdCard {...iconProps} />, // Management occupations
    '1': <Briefcase {...iconProps} />, // Business, finance and administration
    '2': <FlaskConical {...iconProps} />, // Natural and applied sciences
    '3': <HeartPulse {...iconProps} />, // Health occupations
    '4': <Landmark {...iconProps} />, // Education, law, community services
    '5': <Palette {...iconProps} />, // Arts, culture, recreation and sport
    '6': <ShoppingCart {...iconProps} />, // Sales and service occupations
    '7': <Wrench {...iconProps} />, // Trades, transport and equipment operators
    '8': <Sprout {...iconProps} />, // Natural resources, agriculture
    '9': <Cuboid {...iconProps} />, // Manufacturing and utilities
  };
  
  return iconMap[code] || null;
};

/**
 * Get short TEER description from OaSIS code
 * @param oasisCode - OaSIS code (e.g., "82030.01")
 * @returns Short TEER description string, or null
 */
export const getTeerDescriptionShort = (oasisCode: string): string | null => {
  const secondDigit = oasisCode.charAt(1);
  if (secondDigit && /^[0-5]$/.test(secondDigit)) {
    const shortDescriptions: Record<string, string> = {
      '0': 'Management',
      '1': 'University degree',
      '2': 'College/Apprenticeship 2+ years',
      '3': 'College/Apprenticeship <2 years',
      '4': 'Secondary school',
      '5': 'No formal education',
    };
    return shortDescriptions[secondDigit] || null;
  }
  return null;
};

